import { useEffect, useContext, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@ltvco/refresh-lib/vendors';
import { AppConfig, usePermissions } from '@ltvco/refresh-lib/ctx';
import {
  invalidateSession,
  isUserLoggedIn,
  updateSetting,
  useSession,
} from '@ltvco/refresh-lib/v1';
import { useReactivation } from '@ltvco/refresh-lib/payments';
import { Box, CircularProgress } from '@ltvco/refresh-lib/theme';

interface AuthenticatedRouteProps {
  children: JSX.Element;
}

export function AuthenticatedRoute({ children }: AuthenticatedRouteProps) {
  const [isLoading, setIsLoading] = useState(true);
  const {
    session: { isAuthenticated, account },
    setSession,
  } = useSession(); // should be replaced by the user_info object
  const { isFreePlan } = usePermissions();
  const queryClient = useQueryClient();
  const isNonCompliant = account?.account.user_info.noncompliant;
  const { routingUtils, logError } = useContext(AppConfig);
  const { location: locationAppConfig } = routingUtils;

  const location = useLocation();
  const navigate = useNavigate();

  const complianceRoutes = [
    '/compliance-form',
    '/noncompliance',
    '/compliance-prohibited',
    '/compliance-permitted',
    '/compliance-recomply',
  ];

  useReactivation({ currentPathName: location.pathname });

  useEffect(() => {
    if (!account?.account) return;
    if (
      import.meta.env.VITE_TARGET_ENV &&
      !['production', 'staging'].includes(import.meta.env.VITE_TARGET_ENV)
    )
      return;

    // handleOnboardingUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.account]);

  useEffect(() => {
    handleFreshnessHubUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  function handleFreshnessHubUsers() {
    if (!account) return;
    if (account?.account?.staff_info?.staff) return;
    if (
      account?.account.user_info?.design_name === 'freshness' ||
      account?.account.user_info?.design_version === 'v2'
    ) {
      window.location.href = 'https://www.bumper.com/app/';
    }
  }

  // Check if user should be redirected to subscribe page
  useEffect(() => {
    if (!isAuthenticated || !account?.account) return;

    // If user has a paid plan, redirect them from subscribe page
    if (!isFreePlan) {
      if (location.pathname === '/subscribe') {
        navigate('/dashboard');
      }
      return; // End checks for paid users
    }

    const localStorageHideFreemiumUpgradePrompt = localStorage.getItem(
      'hide_freemium_upgrade_prompt'
    );
    const accountHideFreemiumUpgradePrompt =
      account.account.user_settings.hide_freemium_upgrade_prompt;

    if (
      localStorageHideFreemiumUpgradePrompt &&
      !accountHideFreemiumUpgradePrompt
    ) {
      try {
        updateSetting({
          setting: 'hide_freemium_upgrade_prompt',
          state: true,
        });
      } catch (error) {
        logError('Error updating hide_freemium_upgrade_prompt', error as Error);
      }
    }
  }, [isAuthenticated, account, location.pathname, navigate]);

  useEffect(() => {
    // This effect will run once all other checks are complete
    if (account) {
      setIsLoading(false);
    }
  }, [account]);

  function handleOnboardingUsers() {
    // We should redirect all users who have not completed the DQS onboarding
    if (!account?.account) return;
    const sessionOnboardingCompleted = sessionStorage.getItem('dqs_completed');
    if (
      account.account.user_settings.dqs_completed ||
      (sessionOnboardingCompleted && JSON.parse(sessionOnboardingCompleted))
    )
      return;
    const url = location.pathname + location.search + location.hash;
    // Sending the next url to the onboarding page on the
    // location of the AppConfig because when I was sending the values
    // through navigate they weren't being updated with the current path
    // which was causing the user not getting redirected to the correct report generation
    locationAppConfig.state = { next: url };
    navigate('/onboarding');
  }

  useReactivation({ currentPathName: location.pathname });

  if (!isUserLoggedIn()) {
    invalidateSession();
    const url = location.pathname + location.search + location.hash;
    queryClient.clear();

    setTimeout(() => {
      window.location.reload();
    }, 500);

    return <Navigate to="/login" state={{ next: url }} replace />;
  }
  if (isNonCompliant && !complianceRoutes.includes(location.pathname)) {
    return (
      <Navigate to={`/noncompliance`} state={{ from: location }} replace />
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return children;
}
